import * as path from "@tauri-apps/api/path";
import * as taurifs from "@tauri-apps/plugin-fs";
import { type Setter, atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";
import { extname, basename as pathBasename } from "pathe";

import { Logger } from "@moment/logging";
import type { ClientStoreV2 } from "@moment/reboot-differential";

import { env } from "../env/env";
import { getAttachmentsDirPath } from "../paths/paths.internal";
import { $unregisterCsvBinding } from "./csv-bindings.atom";
import { importCsvIntoStore, removeCsvFromStore, tableNameFromFilename } from "./csv-store-sync";

const logger = new Logger("attachments");

// Per-document mutex to serialize add/remove operations.
// Prevents races on filename dedup, file I/O, store mutations, and binding registry.
const documentLocks = new Map<string, Promise<void>>();

export function withDocumentLock<T>(documentId: string, fn: () => Promise<T>): Promise<T> {
	const prev = documentLocks.get(documentId) ?? Promise.resolve();
	let resolve: () => void;
	const next = new Promise<void>((r) => {
		resolve = r;
	});
	documentLocks.set(documentId, next);
	return prev.then(fn).finally(() => resolve!());
}

export interface AttachmentInfo {
	name: string;
}

export const QUERYABLE_EXTENSIONS = new Set([
	".csv",
	// TODO: Re-enable once collaborative SQLite is supported
	// ".sqlite",
	// ".db",
]);

export function isQueryableFile(filename: string): boolean {
	return QUERYABLE_EXTENSIONS.has(extname(filename).toLowerCase());
}

/** True while an add/remove operation is in progress for a given document. */
export const $attachmentsBusy = atomFamily((documentId: string) => {
	const busyAtom = atom(false as boolean);
	busyAtom.debugLabel = `attachmentsBusy/${documentId}`;
	return busyAtom;
});

// ── Shared helper: write bytes to attachments dir + CSV import ─────

/**
 * Write attachment bytes to the attachments directory (deduplicating the
 * filename), then import into the Store if it's a CSV.
 *
 * Returns the final filename (after dedup).  Caller must hold the
 * document lock and call `set($attachmentsList(documentId))` afterward.
 */
async function writeAndImportAttachment(
	documentId: string,
	filename: string,
	contents: Uint8Array,
	store: ClientStoreV2 | undefined,
	set: Setter
): Promise<string> {
	const dirPath = await getAttachmentsDirPath(documentId);

	const dirExists = await taurifs.exists(dirPath);
	if (!dirExists) {
		await taurifs.mkdir(dirPath, { recursive: true });
	}

	const ext = extname(filename);
	const stem = pathBasename(filename, ext);

	let finalName = filename;
	let counter = 1;
	while (await taurifs.exists(await path.join(dirPath, finalName))) {
		finalName = `${stem}-${counter}${ext}`;
		counter++;
	}

	const destPath = await path.join(dirPath, finalName);
	await taurifs.writeFile(destPath, contents);

	set($attachmentsList(documentId));

	if (store && ext.toLowerCase() === ".csv") {
		try {
			await importCsvIntoStore(documentId, finalName, store, set);
		} catch (error) {
			logger.error("Failed to import CSV into store, removing file", {
				documentId,
				filename: finalName,
				error,
			});
			await taurifs.remove(destPath).catch(() => {});
			set($unregisterCsvBinding, { documentId, tableName: tableNameFromFilename(finalName) });
			set($attachmentsList(documentId));
		}
	}

	return finalName;
}

/**
 * Import all CSV files from the attachments directory into a store.
 * Used to hydrate a local ClientStoreV2 on document reopen.
 */
export async function importAllCsvAttachments(
	documentId: string,
	store: ClientStoreV2,
	set: Setter
): Promise<void> {
	if (!env.isDesktopApp || !documentId) {
		return;
	}

	try {
		const dirPath = await getAttachmentsDirPath(documentId);
		if (!(await taurifs.exists(dirPath))) {
			return;
		}

		const entries = await taurifs.readDir(dirPath);
		const csvFiles = entries.filter(
			(e) => e.isFile && extname(e.name).toLowerCase() === ".csv"
		);

		for (const file of csvFiles) {
			await importCsvIntoStore(documentId, file.name, store, set);
		}

		if (csvFiles.length > 0) {
			logger.info("imported CSV attachments from disk", {
				documentId,
				count: csvFiles.length,
			});
		}
	} catch (error) {
		logger.error("failed to import CSV attachments from disk", { documentId, error });
	}
}

export const $attachmentsList = atomFamily((documentId: string) =>
	atomWithRefresh(async (): Promise<AttachmentInfo[]> => {
		if (!env.isDesktopApp || !documentId) {
			return [];
		}

		const dirPath = await getAttachmentsDirPath(documentId);

		try {
			const dirExists = await taurifs.exists(dirPath);
			if (!dirExists) {
				return [];
			}

			const entries = await taurifs.readDir(dirPath);
			return entries
				.filter((entry) => entry.isFile && isQueryableFile(entry.name))
				.map((entry) => ({ name: entry.name }))
				.sort((a, b) => a.name.localeCompare(b.name));
		} catch (error) {
			logger.error("Failed to list attachments", { documentId, error });
			return [];
		}
	})
);

export const $removeAttachment = atom(
	null,
	async (
		_get,
		set,
		{
			documentId,
			filename,
			store,
		}: {
			documentId: string;
			filename: string;
			store?: ClientStoreV2;
		}
	) => {
		set($attachmentsBusy(documentId), true);
		try {
			await withDocumentLock(documentId, async () => {
				const dirPath = await getAttachmentsDirPath(documentId);
				const filePath = await path.join(dirPath, filename);

				await taurifs.remove(filePath);

				if (store && extname(filename).toLowerCase() === ".csv") {
					removeCsvFromStore(filename, store);
					set($unregisterCsvBinding, {
						documentId,
						tableName: tableNameFromFilename(filename),
					});
				}

				set($attachmentsList(documentId));
			});
		} finally {
			set($attachmentsBusy(documentId), false);
		}
	}
);

export const $addAttachmentFromPath = atom(
	null,
	async (
		_get,
		set,
		{
			documentId,
			sourcePath,
			store,
		}: {
			documentId: string;
			sourcePath: string;
			store?: ClientStoreV2;
		}
	) => {
		logger.info("$addAttachmentFromPath", { documentId, sourcePath, hasStore: !!store });

		const rawFilename = await path.basename(sourcePath);
		if (!isQueryableFile(rawFilename)) {
			throw new Error(
				`Unsupported file type. Only ${[...QUERYABLE_EXTENSIONS].join(", ")} files are supported.`
			);
		}

		set($attachmentsBusy(documentId), true);
		try {
			await withDocumentLock(documentId, async () => {
				const contents = await taurifs.readFile(sourcePath);
				await writeAndImportAttachment(documentId, rawFilename, contents, store, set);
			});
		} finally {
			set($attachmentsBusy(documentId), false);
		}
	}
);

export const $addAttachmentFromBytes = atom(
	null,
	async (
		_get,
		set,
		{
			documentId,
			filename,
			contents,
			store,
		}: {
			documentId: string;
			filename: string;
			contents: Uint8Array;
			store?: ClientStoreV2;
		}
	) => {
		logger.info("$addAttachmentFromBytes", { documentId, filename, hasStore: !!store });

		if (!isQueryableFile(filename)) {
			throw new Error(
				`Unsupported file type. Only ${[...QUERYABLE_EXTENSIONS].join(", ")} files are supported.`
			);
		}

		set($attachmentsBusy(documentId), true);
		try {
			await withDocumentLock(documentId, async () => {
				await writeAndImportAttachment(documentId, filename, contents, store, set);
			});
		} finally {
			set($attachmentsBusy(documentId), false);
		}
	}
);
